import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Loader2, MapPin, NotebookPen, Phone } from 'lucide-react';
import api from '../api';
import { RecertVisitActions } from './RecertVisitActions';
import { VisitNoteForm } from './VisitNotes';

const isoDay = (offset) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toLocaleDateString('en-CA'); };

const Visit = ({ v, reload }) => {
  const navigate = useNavigate();
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <li className="px-4 py-3" data-testid={`agenda-visit-${v.id}`}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
        <span className="w-20 font-semibold text-gray-900">{v.time}</span>
        <div className="flex-1 min-w-[12rem]">
          <button onClick={() => navigate(`/dashboard/patients/demographics/${v.patientId}`)} className="font-medium text-gray-800 hover:underline" data-testid={`agenda-patient-${v.id}`}>
            {v.patientName}
          </button>
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{v.type}</span>
          {v.status === 'Completed' && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Done</span>}
          <div className="flex flex-wrap gap-x-4 mt-1 text-sm text-gray-600">
            {v.address && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.address)}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#b8860b]" data-testid={`agenda-address-${v.id}`}><MapPin className="w-3.5 h-3.5" />{v.address}</a>
            )}
            {v.phone && (
              <a href={`tel:${v.phone}`} className="inline-flex items-center gap-1 hover:text-[#b8860b]" data-testid={`agenda-phone-${v.id}`}><Phone className="w-3.5 h-3.5" />{v.phone}</a>
            )}
          </div>
        </div>
      </div>
      {v.type === 'Recertification' && v.recertPeriodEnd && <RecertVisitActions appointment={v} onChanged={reload} />}
      <button onClick={() => setNoteOpen(o => !o)} data-testid={`agenda-add-note-${v.id}`}
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-[#d4af37] text-[#8a6d1a] hover:bg-amber-50">
        <NotebookPen className="w-4 h-4" /> {noteOpen ? 'Hide Visit Note' : 'Add Visit Note'}{v.noteCount ? ` (${v.noteCount})` : ''}
      </button>
      {noteOpen && <VisitNoteForm appointmentId={v.id} onSaved={reload} />}
    </li>
  );
};

export const TodayAgenda = () => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const load = () => api.get(`/agenda?date=${isoDay(offset)}`).then(r => setData(r.data)).catch(() => setData({ visits: [], isNurse: false }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [offset]);

  if (data && !data.isNurse && data.visits.length === 0 && offset === 0) return null;
  return (
    <div className="bg-white rounded border border-gray-200 shadow-sm" data-testid="today-agenda">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 rounded-t">
        <h3 className="font-semibold text-[#d4af37] flex items-center gap-2"><CalendarDays className="w-4 h-4" /> My Visits</h3>
        <div className="flex gap-1">
          {['Today', 'Tomorrow'].map((l, i) => (
            <button key={l} onClick={() => setOffset(i)} data-testid={`agenda-${l.toLowerCase()}`}
              className={`px-3 py-0.5 rounded-full text-xs font-semibold ${offset === i ? 'bg-[#d4af37] text-gray-900' : 'text-white/80 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin m-4" /> : data.visits.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500" data-testid="agenda-empty">No visits scheduled {offset ? 'tomorrow' : 'today'}.</p>
      ) : (
        <ul className="divide-y">{data.visits.map(v => <Visit key={v.id} v={v} reload={load} />)}</ul>
      )}
    </div>
  );
};
